import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { bulkResetBreadCrumpData, checkPermission } from "../../../config/utils";
import history from "../../../config/history";
import StarActiveIcon from "../../../assets/img/star-active.svg";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Portal from "../../common/components/Portal";
import { addToQuickLinks, deleteQuickLinks, updateQuickLink, getQuickLinks } from "../actions";
import { useDispatch, useSelector } from "react-redux";
import * as projectService from "../../project/services";
import Loader from "../../common/components/Loader";
import { EllipsisTooltipText } from "../../common/components/EllipsisTextWithTooltip";
import { permissions } from "../../../config/permissions";
import { navigateWithPrompts } from "../../../utils/navigationHelpers";
import {
    getCapitalRequestLabel,
    getQuickLinkDisplayName,
    getQuickLinkStoredName,
    pluralizeLabel
} from "../../../utils/capitalRequestLabel";

const MOCK_ACTIONS = [
    {
        id: "mock-add-recommendation",
        name: "Add Recommendation",
        type: "action",
        key: ["forms", permissions.RECOMMENDATIONS, "create"],
        url: "recommendations/add"
    },
    {
        id: "mock-add-asset",
        name: "Add Asset",
        type: "action",
        key: ["forms", "assets", "create"],
        url: "assets/add"
    },
    {
        id: "mock-add-cr",
        name: "Add Capital Request",
        type: "action",
        key: ["forms", permissions.CR_RECOMMENDATIONS, "create"],
        url: "capital-requests/add"
    },
    {
        id: "mock-add-report",
        name: "Add Report",
        type: "action",
        key: ["forms", permissions.SMART_CHARTS, "create"],
        url: "smartcharts/new"
    },
    {
        id: "mock-export-dashboard-report",
        name: "Export Dashboard Report",
        type: "action",
        key: ["forms", permissions.SMART_CHARTS, "export"],
        url: "dashboard/export-report"
    },
    {
        id: "mock-export-recommendations",
        name: "Export Recommendations",
        type: "action",
        key: ["forms", permissions.RECOMMENDATIONS, "export"],
        url: "recommendations/export"
    },
    {
        id: "mock-add-building",
        name: "Add Building",
        type: "action",
        key: ["forms", "buildings", "create"],
        url: "building/add"
    },
    {
        id: "mock-add-site",
        name: "Add Site",
        type: "action",
        key: ["forms", "sites", "create"],
        url: "site/add"
    },
    {
        id: "mock-add-region",
        name: "Add Region",
        type: "action",
        key: ["forms", "regions", "create"],
        url: "region/add"
    },
    {
        id: "mock-add-project",
        name: "Add Project",
        type: "action",
        key: ["forms", "fca_projects", "create"],
        url: "project/add"
    },
    {
        id: "mock-add-document",
        name: "Add Document",
        type: "action",
        key: ["forms", "documents", "create"],
        url: "documents/add"
    },
    {
        id: "mock-upload-image",
        name: "Upload Image",
        type: "action",
        key: ["forms", "image_management", "create"],
        url: "images"
    }
];

const SPECIAL_BOOKMARK_URLS = {
    savedFilters: "dashboard/saved-filters"
};
const REPORT_TAB_BOOKMARK_URLS = {
    "dashboard/favorite-reports": "reports",
    "dashboard/reporttemplates": "reporttemplates",
    "dashboard/reports": "reports",
    "dashboard/corereports": "corereports",
    "dashboard/taskmanager": "taskmanager"
};
const normalizeQuickLinkUrl = url => (url || "").replace(/^\//, "").toLowerCase();

const CONTEXT_SCOPED_SECTIONS = ["projectinfo", "assetinfo", "energyinfo"];
const isContextScopedQuickLink = url => CONTEXT_SCOPED_SECTIONS.includes((url || "").split("/")[1]);

const CLIENT_DYNAMIC_TABLE_PREFIX = "/client-dynamic-tables/";

const isPublishedTableAvailable = (item, publishedResponse) => {
    const url = (item?.url || "").toLowerCase();
    if (!url.startsWith(CLIENT_DYNAMIC_TABLE_PREFIX)) return true;
    if (!publishedResponse?.success) return true;
    const tableId = url.slice(CLIENT_DYNAMIC_TABLE_PREFIX.length).split("/")[0];
    return (publishedResponse.dynamic_tables || []).some(table => String(table.id).toLowerCase() === tableId);
};

const DASHBOARD_BREAD_CRUMB = { key: "dashboard", name: "Dashboard", path: "/dashboard", index: 0 };

export const QuickLinks = ({
    projectId = "",
    projectName = "",
    clientId = "",
    clientName = "",
    consultancyId = "",
    ReactTooltip,
    openSavedFiltersModal,
    openReportTabModal,
    openDashboardReportExportModal,
    rootBreadCrumb = DASHBOARD_BREAD_CRUMB
}) => {
    const dispatch = useDispatch();
    const quickLinks = useSelector(state => state.dashboardReducer?.getQuickLinksResponse);
    const publishedDynamicTables = useSelector(state => state.dynamicTableReducer?.getPublishedDynamicTablesResponse);
    const projectDetails = useSelector(state => state.projectReducer?.getProjectByIdResponse);
    const miscellaneous = projectDetails?.miscellaneous;
    const capitalRequestLabel = getCapitalRequestLabel(miscellaneous);
    const capitalRequestsLabel = pluralizeLabel(capitalRequestLabel);

    const getDisplayName = item => getQuickLinkDisplayName(item, miscellaneous);
    const [quickLinksConfirmationModal, setQuickLinksConfirmationModal] = useState({ show: false, item: null });
    const [links, setLinks] = useState([]);
    const [actions, setActions] = useState([]);
    const [isBookmarksOpen, setIsBookmarksOpen] = useState(false);
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const [editingLinkId, setEditingLinkId] = useState(null);
    const [editedLabel, setEditedLabel] = useState("");
    const [editingActionId, setEditingActionId] = useState(null);
    const [editedActionLabel, setEditedActionLabel] = useState("");
    const [showAvailableActions, setShowAvailableActions] = useState(false);
    const [fetchedConsultancyId, setFetchedConsultancyId] = useState("");
    const bookmarksRef = useRef(null);
    const actionsRef = useRef(null);
    const availableActionsRef = useRef(null);

    const { isFormDirty } = useSelector(state => state.commonReducer || {});

    useEffect(() => {
        if (quickLinks?.data?.length) {
            const formatted = quickLinks.data
                .map(item => {
                    if (!item.bread_crumbs && item.type !== "action") {
                        const breadCrumbs = getBreadCrumbs(item);
                        return { ...item, bread_crumbs: JSON.stringify(breadCrumbs) };
                    }
                    return item;
                })
                .filter(item => isPublishedTableAvailable(item, publishedDynamicTables))
                .filter(item => {
                    if (item.key) {
                        try {
                            const parsedKeys = JSON.parse(item.key);
                            if (Array.isArray(parsedKeys) && parsedKeys.length === 3) {
                                return checkPermission(...parsedKeys);
                            } else {
                                console.error("permissionKeys must be an array of 3 values:", parsedKeys);
                                return false;
                            }
                        } catch (error) {
                            console.error("Error parsing permissionKeys:", error);
                            return false;
                        }
                    }
                    return true;
                });

            const apiActions = formatted.filter(item => item.type === "action");
            const apiLinks = formatted.filter(item => item.type !== "action");

            setLinks(prev => (JSON.stringify(prev) !== JSON.stringify(apiLinks) ? apiLinks : prev));
            setActions(prev => (JSON.stringify(prev) !== JSON.stringify(apiActions) ? apiActions : prev));
        } else if (quickLinks && !quickLinks.loading) {
            setLinks(prev => (prev.length ? [] : prev));
            setActions(prev => (prev.length ? [] : prev));
        }
    }, [quickLinks, projectId, clientId, publishedDynamicTables]);

    useEffect(() => {
        const fetchConsultancy = async () => {
            if (!projectId || consultancyId || fetchedConsultancyId) {
                return;
            }
            if (String(projectDetails?.id || "") === String(projectId || "") && projectDetails?.consultancy?.id) {
                setFetchedConsultancyId(projectDetails.consultancy.id);
                return;
            }
            try {
                const res = await projectService.getProjectById(projectId);
                if (res?.data?.consultancy?.id) {
                    setFetchedConsultancyId(res.data.consultancy.id);
                }
            } catch (error) {
                console.error("Failed to fetch default consultancy ID", error);
            }
        };
        fetchConsultancy();
    }, [projectId, consultancyId, fetchedConsultancyId, projectDetails]);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (userId && (!quickLinks || (!quickLinks.data && !quickLinks.loading))) {
            dispatch(getQuickLinks({ user_id: userId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (isBookmarksOpen || isActionsOpen) {
            ReactTooltip.rebuild();
        }
    }, [isBookmarksOpen, isActionsOpen, quickLinks]);

    useEffect(() => {
        const navbar = bookmarksRef.current?.closest(".navbarOtr") || actionsRef.current?.closest(".navbarOtr");
        if (!navbar) return undefined;

        const isQuickLinksOpen = isBookmarksOpen || isActionsOpen;
        navbar.classList.toggle("quicklinks-layer-active", isQuickLinksOpen);

        return () => {
            navbar.classList.remove("quicklinks-layer-active");
        };
    }, [isBookmarksOpen, isActionsOpen]);

    useEffect(() => {
        const handleOutsideClick = e => {
            if (bookmarksRef.current && !bookmarksRef.current.contains(e.target) && !quickLinksConfirmationModal.show) {
                setIsBookmarksOpen(false);
            }
            if (actionsRef.current && !actionsRef.current.contains(e.target) && !quickLinksConfirmationModal.show) {
                setIsActionsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [quickLinksConfirmationModal.show]);

    const handleView = (e, link) => {
        e.preventDefault();
        const linkUrl = normalizeQuickLinkUrl(link?.url);
        if (linkUrl === SPECIAL_BOOKMARK_URLS.savedFilters) {
            setIsBookmarksOpen(false);
            ReactTooltip.hide();
            if (openSavedFiltersModal) {
                openSavedFiltersModal();
            } else {
                history.push("/dashboard", { openSavedFilters: true });
            }
            return;
        }
        const reportTab = REPORT_TAB_BOOKMARK_URLS[linkUrl];
        if (reportTab) {
            setIsBookmarksOpen(false);
            ReactTooltip.hide();
            if (openReportTabModal) {
                openReportTabModal(reportTab);
            } else {
                history.push("/dashboard", { openReportTab: reportTab });
            }
            return;
        }
        if (!projectId) return;
        navigateWithPrompts({
            isFormDirty,
            navigateAction: () => {
                setIsBookmarksOpen(false);
                ReactTooltip.hide();
                try {
                    let bc = [];
                    bc = [{ ...rootBreadCrumb }];
                    const recomputed = isContextScopedQuickLink(link.url) ? getBreadCrumbs(link) : null;
                    const parsedBc = recomputed?.length ? recomputed : JSON.parse(link.bread_crumbs);
                    bc = [...bc, ...parsedBc];
                    const urlPath = bc[bc.length - 1].path;
                    history.push(urlPath, { from: "dashboard", fromQuickLink: true });
                    bulkResetBreadCrumpData(bc);
                } catch (error) {
                    console.log("Error while viewing quick link:", error);
                }
            }
        });
    };

    const toggleFavorite = item => {
        setIsBookmarksOpen(false);
        setIsActionsOpen(false);
        setShowAvailableActions(false);
        ReactTooltip.hide();
        setQuickLinksConfirmationModal({ show: true, item });
    };

    const handleRemoveFromQuickLinks = () => {
        const { item } = quickLinksConfirmationModal;
        if (item) {
            const userId = localStorage.getItem("userId");
            dispatch(deleteQuickLinks(item.id, userId, item.type));
        }
        setQuickLinksConfirmationModal({ show: false, item: null });
    };

    const handleRenameClick = link => {
        setEditingLinkId(link.id);
        setEditedLabel(link.name);
    };

    const handleRenameSubmit = linkId => {
        const trimmedLabel = editedLabel.trim();
        if (!trimmedLabel) return;
        const userId = localStorage.getItem("userId");
        dispatch(updateQuickLink(linkId, { name: trimmedLabel, user_id: userId }));
        setLinks(links.map(link => (link.id === linkId ? { ...link, name: trimmedLabel } : link)));
        setEditingLinkId(null);
        setEditedLabel("");
    };

    const onDragEnd = result => {
        const { source, destination } = result;
        if (!destination) return;

        const reorderedLinks = Array.from(links);
        const [movedItem] = reorderedLinks.splice(source.index, 1);
        reorderedLinks.splice(destination.index, 0, movedItem);

        setLinks(reorderedLinks);

        const combinedList = [...reorderedLinks, ...actions];
        const orderedList = combinedList.map((item, index) => ({ id: item.id, sort_order: index + 1 }));

        const userId = localStorage.getItem("userId");
        dispatch(updateQuickLink(orderedList[0]?.id, { order: orderedList, user_id: userId }));
    };

    const handleActionRenameClick = action => {
        setEditingActionId(action.id);
        setEditedActionLabel(action.name);
    };

    const handleActionRenameSubmit = actionId => {
        const trimmedLabel = editedActionLabel.trim();
        if (!trimmedLabel) return;
        const userId = localStorage.getItem("userId");
        dispatch(updateQuickLink(actionId, { name: trimmedLabel, user_id: userId }));
        setActions(actions.map(action => (action.id === actionId ? { ...action, name: trimmedLabel } : action)));
        setEditingActionId(null);
        setEditedActionLabel("");
    };

    const onActionsDragEnd = result => {
        const { source, destination } = result;
        if (!destination) return;

        const reorderedActions = Array.from(actions);
        const [movedItem] = reorderedActions.splice(source.index, 1);
        reorderedActions.splice(destination.index, 0, movedItem);

        setActions(reorderedActions);

        const combinedList = [...links, ...reorderedActions];
        const orderedList = combinedList.map((item, index) => ({ id: item.id, sort_order: index + 1 }));

        const userId = localStorage.getItem("userId");
        dispatch(updateQuickLink(orderedList[0]?.id, { order: orderedList, user_id: userId }));
    };

    const addActionToQuickLinks = action => {
        const userId = localStorage.getItem("userId");
        const params = {
            user_id: userId,
            name: getQuickLinkStoredName(action.name, action.url),
            url: action.url,
            type: "action",
            key: action.key ? JSON.stringify(action.key) : null
        };
        dispatch(addToQuickLinks(params));
        setShowAvailableActions(false);
    };

    const renderQuickLinksConfirmationModal = () => {
        if (!quickLinksConfirmationModal.show) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={
                            quickLinksConfirmationModal.item?.type === "action"
                                ? "Are you sure you want to remove this action from Actions?"
                                : "Are you sure you want to remove this menu from Bookmarks?"
                        }
                        message=""
                        type="cancel"
                        onNo={() => setQuickLinksConfirmationModal({ show: false, item: null })}
                        onYes={handleRemoveFromQuickLinks}
                    />
                }
                onCancel={() => setQuickLinksConfirmationModal({ show: false, item: null })}
            />
        );
    };

    const toggleBookmarksDropdown = () => {
        setIsBookmarksOpen(prevState => !prevState);
        setIsActionsOpen(false);
        ReactTooltip.hide();
    };

    const toggleActionsDropdown = () => {
        setIsActionsOpen(prevState => !prevState);
        setIsBookmarksOpen(false);
        setShowAvailableActions(false);
        ReactTooltip.hide();
    };

    const getBreadCrumbs = link => {
        let bc = [];
        const paths = link.url?.split("/") || [];
        const tab = paths[paths.length - 1];
        const section = paths[1];
        if (section === "projectinfo") {
            bc = [
                { key: "projectName", name: projectName, path: `/project/projectinfo/${projectId}/basicdetails`, index: 1 },
                {
                    key: "info",
                    name: getDisplayName(link),
                    path: `/project/projectinfo/${projectId}/${tab}`,
                    index: 2
                }
            ];
        } else if (section === "assetinfo") {
            bc = [
                { key: "assetName", name: clientName, path: `/assetmanagement/assetinfo/${clientId}/basicdetails`, index: 1 },
                { key: "info", name: link.name, path: `/assetmanagement/assetinfo/${clientId}/${tab}`, index: 2 }
            ];
        } else if (section === "energyinfo") {
            bc = [
                { key: "energyName", name: clientName, path: `/energymanagement/energyinfo/${clientId}/basicdetails`, index: 1 },
                { key: "info", name: link.name, path: `/energymanagement/energyinfo/${clientId}/${tab}`, index: 2 }
            ];
        }
        return bc;
    };

    const resolvedConsultancyId = consultancyId || fetchedConsultancyId;

    const ACTION_SECTION_MAP = {
        recommendations: {
            sectionName: "Recommendations",
            sectionPath: () => `/project/projectinfo/${projectId}/recommendations`,
            actionPath: () => `/recommendations/add?c_id=${clientId}&p_id=${projectId}&type=Regular`
        },
        "capital-requests": {
            sectionName: capitalRequestsLabel,
            sectionPath: () => `/project/projectinfo/${projectId}/recommendations?is_cr=true`,
            actionPath: () => `/recommendations/add?c_id=${clientId}&is_cr=true&p_id=${projectId}&type=Regular`
        },
        assets: {
            sectionName: "Assets",
            sectionPath: () => `/project/projectinfo/${projectId}/assets`,
            actionPath: () => `/assets/add?c_id=${clientId}&p_id=${projectId}`
        },
        site: {
            sectionName: "Sites",
            sectionPath: () => `/project/projectinfo/${projectId}/sites`,
            actionPath: () => `/site/add?r_id=&c_id=${clientId}&cty_id=${resolvedConsultancyId}&p_id=${projectId}`
        },
        building: {
            sectionName: "Buildings",
            sectionPath: () => `/project/projectinfo/${projectId}/buildings`,
            actionPath: () => `/building/add?s_id=&r_id=&c_id=${clientId}&cty_id=${resolvedConsultancyId}&p_id=${projectId}`
        },
        region: {
            sectionName: "Regions",
            sectionPath: () => `/project/projectinfo/${projectId}/regions`,
            actionPath: () => `/region/add?c_id=${clientId}&cty_id=${resolvedConsultancyId}&p_id=${projectId}&r_id=`
        },
        project: {
            sectionName: "FCA Projects",
            sectionPath: () => `/project`,
            actionPath: () => `/project/add`
        },
        documents: {
            sectionName: "Documents",
            sectionPath: () => `/project/projectinfo/${projectId}/documents`,
            actionPath: () => `/documents/add?cty_id=${resolvedConsultancyId}&c_id=${clientId}&p_id=${projectId}`
        },
        images: {
            sectionName: "Images",
            sectionPath: () => `/images`,
            actionPath: () => `/images?upload=true`
        },
        smartcharts: {
            sectionName: "Reports",
            sectionPath: () => `/smartcharts`,
            actionPath: () => `/smartcharts/new`,
            getBreadcrumbs: action => [
                { ...rootBreadCrumb },
                {
                    key: "main",
                    name: "Smart Reports & Charts",
                    path: "/smartcharts/reporttemplates",
                    index: 1
                },
                {
                    key: "reporttemplates",
                    name: "Report Templates",
                    path: "/smartcharts/reporttemplates",
                    index: 2
                },
                {
                    key: "add",
                    name: "Create New Smart Chart Report Template",
                    path: `/smartcharts/new`,
                    index: 3
                }
            ]
        }
    };

    const getActionBreadCrumbs = action => {
        const urlPrefix = (action.url || "").split("/")[0]; // e.g. "recommendations"
        const config = ACTION_SECTION_MAP[urlPrefix];
        if (!config) return [];
        if (config.getBreadcrumbs) {
            return config.getBreadcrumbs(action);
        }
        return [
            { ...rootBreadCrumb },
            { key: "projectName", name: projectName, path: `/project/projectinfo/${projectId}/basicdetails`, index: 1 },
            { key: "info", name: config.sectionName, path: config.sectionPath(), index: 2 },
            { key: "add", name: getDisplayName(action), path: config.actionPath(), index: 3 }
        ];
    };

    const handleActionView = (e, action) => {
        e.preventDefault();

        const navigateAction = () => {
            setIsActionsOpen(false);
            setShowAvailableActions(false);
            ReactTooltip.hide();
            if (action.url === "dashboard/export-report") {
                if (openDashboardReportExportModal) {
                    openDashboardReportExportModal();
                } else {
                    history.push("/dashboard", { openDashboardReportExport: true });
                }
                return;
            }
            if (action.url === "recommendations/export") {
                if (!projectId) return;
                const bc = [
                    { ...rootBreadCrumb },
                    { key: "projectName", name: projectName, path: `/project/projectinfo/${projectId}/basicdetails`, index: 1 },
                    { key: "info", name: "Recommendations", path: `/project/projectinfo/${projectId}/recommendations`, index: 2 }
                ];
                history.push(`/project/projectinfo/${projectId}/recommendations`, { from: "dashboard", openRecommendationExport: true });
                bulkResetBreadCrumpData(bc);
                return;
            }
            const urlPrefix = (action.url || "").split("/")[0];
            if (!ACTION_SECTION_MAP[urlPrefix]) {
                history.push(`/${action.url}`);
                return;
            }

            if (!projectId) return;
            try {
                const bc = getActionBreadCrumbs(action);
                if (!bc.length) return;
                const urlPath = bc[bc.length - 1].path;
                history.push(urlPath, { from: "dashboard" });
                bulkResetBreadCrumpData(bc);
            } catch (error) {
                console.log("Error while navigating to action:", error);
            }
        };

        navigateWithPrompts({
            isFormDirty,
            navigateAction
        });
    };

    const availableActions = MOCK_ACTIONS.filter(
        mockAction => !actions.find(action => action.url === mockAction.url) && checkPermission(...mockAction.key)
    ).map(mockAction =>
        mockAction.id === "mock-add-cr" ? { ...mockAction, name: `Add ${capitalRequestLabel}` } : mockAction
    );

    return (
        <>
            <div className={`dropdown quicklink-drop mr-2`} ref={bookmarksRef}>
                <button
                    onClick={toggleBookmarksDropdown}
                    data-tip={isBookmarksOpen ? "" : "View Bookmarks"}
                    data-for="dashboard-icons-right"
                    className="btn btn-primary dropdown-toggle"
                    type="button"
                    id="bookmarksDropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    <i className="fas fa-bookmark mr-1" />
                    <span> Bookmarks </span>
                </button>

                {isBookmarksOpen && (
                    <div className="dropdown-menu show" aria-labelledby="bookmarksDropdown">
                        <div className="quick-links-section w-100 p-2">
                            <h6 className="quick-section-title">
                                Bookmarks
                            </h6>
                            {links.length ? (
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="quickLinksList">
                                        {provided => (
                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                                {links.map((link, index) => (
                                                    <Draggable key={link.id} draggableId={link.id} index={index}>
                                                        {provided => (
                                                            <div
                                                                className="d-flex align-items-center wrapper-drop"
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                            >
                                                                <img
                                                                    src="/img/Group 2.svg"
                                                                    alt="Drag Icon"
                                                                    {...provided.dragHandleProps}
                                                                    style={{ cursor: "grab", marginRight: "" }}
                                                                />
                                                                {editingLinkId === link.id ? (
                                                                    <input
                                                                        type="text"
                                                                        value={editedLabel}
                                                                        onChange={e => setEditedLabel(e.target.value)}
                                                                        // onBlur={() => setEditingLinkId(null)}
                                                                        onKeyDown={e => {
                                                                            if (e.key === "Enter") handleRenameSubmit(link.id);
                                                                            if (e.key === "Escape") setEditingLinkId(null);
                                                                        }}
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className="d-flex align-items-center drop-txt"
                                                                        onClick={e => handleView(e, link)}
                                                                    >
                                                                        <EllipsisTooltipText
                                                                            className={`dropdown-item ${!projectId ? "cursor-disabled" : ""}`}
                                                                            isAnchorTag
                                                                            text={getDisplayName(link)}
                                                                            href={`#${link.key}`}
                                                                        />
                                                                        <i
                                                                            data-for="dashboard-icons-right"
                                                                            onClick={e => e.stopPropagation()}
                                                                            className="fas fa-info-circle"
                                                                            data-tip={`URL: ${
                                                                                link.url?.startsWith("/") ? link.url : `/${link.url}`
                                                                            }<br />Type: ${link.url?.startsWith("/") ? "Menu" : "Tab"}`}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div className="icons-drop-quick">
                                                                    {editingLinkId === link.id ? (
                                                                        <>
                                                                            <i
                                                                                key="saveButton"
                                                                                className="edit-icon ml-2 mr-2 fas fa-check"
                                                                                role="button"
                                                                                data-for="dashboard-icons-right"
                                                                                data-tip={editedLabel.trim() ? "Save" : "Enter a name to save"}
                                                                                onClick={e => {
                                                                                    handleRenameSubmit(link.id);
                                                                                }}
                                                                                style={{
                                                                                    opacity: editedLabel.trim() ? 1 : 0.5,
                                                                                    pointerEvents: editedLabel.trim() ? "auto" : "none",
                                                                                    cursor: editedLabel.trim() ? "pointer" : "not-allowed"
                                                                                }}
                                                                            />
                                                                            <i
                                                                                key="cancelButton"
                                                                                className="edit-icon fas fa-times"
                                                                                role="button"
                                                                                data-for="dashboard-icons-right"
                                                                                data-tip="Cancel"
                                                                                onClick={e => {
                                                                                    setEditingLinkId(null);
                                                                                }}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i
                                                                                key="editButton"
                                                                                className="edit-icon ml-2  mr-2 fas fa-pencil-alt"
                                                                                role="button"
                                                                                data-for="dashboard-icons-right"
                                                                                data-tip="Rename"
                                                                                onClick={e => {
                                                                                    handleRenameClick(link);
                                                                                }}
                                                                            />
                                                                            <i
                                                                                key="favoriteButton"
                                                                                className="favourite-icon"
                                                                                role="button"
                                                                                data-tip="Remove from Bookmarks"
                                                                                data-for="dashboard-icons-right"
                                                                                onClick={e => {
                                                                                    toggleFavorite(link);
                                                                                }}
                                                                            >
                                                                                <img src={StarActiveIcon} alt="Remove from Bookmarks" />
                                                                            </i>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}

                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            ) : quickLinks?.loading ? (
                                <Loader />
                            ) : (
                                <div>No Bookmarks Found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={`dropdown quicklink-drop`} ref={actionsRef}>
                <button
                    onClick={toggleActionsDropdown}
                    data-tip={isActionsOpen ? "" : "View Actions"}
                    data-for="dashboard-icons-right"
                    className="btn btn-primary dropdown-toggle"
                    type="button"
                    id="actionsDropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    <i className="fas fa-bolt mr-1"></i>
                    <span> Actions </span>
                </button>

                {isActionsOpen && (
                    <div className="dropdown-menu show" aria-labelledby="actionsDropdown">
                        <div className="quick-actions-section w-100 p-2">
                            <h6 className="quick-section-title">
                                Actions
                            </h6>
                            {actions.length ? (
                                <DragDropContext onDragEnd={onActionsDragEnd}>
                                    <Droppable droppableId="quickActionsList">
                                        {provided => (
                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                                {actions.map((action, index) => {
                                                    return (
                                                        <Draggable key={action.id} draggableId={String(action.id)} index={index}>
                                                            {provided => (
                                                                <div
                                                                    className="d-flex align-items-center wrapper-drop"
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                >
                                                                    <img
                                                                        src="/img/Group 2.svg"
                                                                        alt="Drag Icon"
                                                                        {...provided.dragHandleProps}
                                                                        style={{ cursor: "grab" }}
                                                                    />
                                                                    {editingActionId === action.id ? (
                                                                        <input
                                                                            type="text"
                                                                            value={editedActionLabel}
                                                                            onChange={e => setEditedActionLabel(e.target.value)}
                                                                            onKeyDown={e => {
                                                                                if (e.key === "Enter") handleActionRenameSubmit(action.id);
                                                                                if (e.key === "Escape") setEditingActionId(null);
                                                                            }}
                                                                            autoFocus
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="d-flex align-items-center drop-txt"
                                                                            onClick={e => handleActionView(e, action)}
                                                                        >
                                                                            <EllipsisTooltipText
                                                                                className={`dropdown-item ${!projectId && ACTION_SECTION_MAP[(action.url || "").split("/")[0]] ? "cursor-disabled" : ""}`}
                                                                                isAnchorTag
                                                                                text={getDisplayName(action)}
                                                                                href="#"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <div className="icons-drop-quick">
                                                                        {editingActionId === action.id ? (
                                                                            <>
                                                                                <i
                                                                                    className="edit-icon ml-2 mr-2 fas fa-check"
                                                                                    onClick={() => handleActionRenameSubmit(action.id)}
                                                                                    data-tip={editedActionLabel.trim() ? "Save" : "Enter a name to save"}
                                                                                    data-for="dashboard-icons-right"
                                                                                    style={{
                                                                                        opacity: editedActionLabel.trim() ? 1 : 0.5,
                                                                                        pointerEvents: editedActionLabel.trim() ? "auto" : "none",
                                                                                        cursor: editedActionLabel.trim() ? "pointer" : "not-allowed"
                                                                                    }}
                                                                                />
                                                                                <i
                                                                                    className="edit-icon fas fa-times"
                                                                                    onClick={() => setEditingActionId(null)}
                                                                                    data-tip="Cancel"
                                                                                    data-for="dashboard-icons-right"
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i
                                                                                    className="edit-icon ml-2 mr-2 fas fa-pencil-alt"
                                                                                    onClick={() => handleActionRenameClick(action)}
                                                                                    data-tip="Rename"
                                                                                    data-for="dashboard-icons-right"
                                                                                />
                                                                                <i
                                                                                    className="favourite-icon"
                                                                                    onClick={() => toggleFavorite(action)}
                                                                                    data-tip="Remove Action"
                                                                                    data-for="dashboard-icons-right"
                                                                                >
                                                                                    <img src={StarActiveIcon} alt="Remove Action" />
                                                                                </i>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            ) : quickLinks?.loading ? (
                                <Loader />
                            ) : (
                                <div className="p-2 text-center text-muted">No Actions Found</div>
                            )}

                            <div
                                className="mt-2 text-center"
                                style={{ position: "sticky", bottom: "-10px", zIndex: 3, background: "#fff", padding: "8px 0" }}
                            >
                                <button
                                    className="btn btn-sm btn-outline-primary w-100"
                                    onClick={() => {
                                        const next = !showAvailableActions;
                                        setShowAvailableActions(next);
                                        if (next) {
                                            setTimeout(() => {
                                                availableActionsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                                            }, 50);
                                        }
                                    }}
                                >
                                    <i className={`fas fa-${showAvailableActions ? "minus" : "plus"} mr-1`}></i>
                                    {showAvailableActions ? "Cancel" : "Add More Actions"}
                                </button>
                            </div>

                            {showAvailableActions && (
                                <div
                                    ref={availableActionsRef}
                                    className="available-actions-list border-top mt-2 pt-2"
                                    style={{ maxHeight: "200px", overflowY: "auto" }}
                                >
                                    <h6
                                        className="quick-section-title"
                                    >
                                        Available Actions
                                    </h6>
                                    {availableActions.length ? (
                                        availableActions.map(action => (
                                            <div
                                                className="d-flex align-items-center wrapper-drop justify-content-between cursor-pointer"
                                                key={action.id}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    addActionToQuickLinks(action);
                                                }}
                                                style={{ padding: "8px" }}
                                            >
                                                <div className="drop-txt">{action.name}</div>
                                                <i className="fas fa-plus text-primary"></i>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-2 text-center text-muted col-12 w-100 d-inline-block">No more actions available</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {renderQuickLinksConfirmationModal()}
        </>
    );
};
